/**
 * The status filter: All, Live, Scheduled, Disabled, Ended, each with its
 * count. A real tablist, so it is one tab stop and the arrow keys move between
 * the tabs.
 */
import { useRef, type KeyboardEvent } from 'react';

import {
  CAMPAIGN_TAB_LABELS,
  CAMPAIGN_TABS,
  type CampaignListTab,
} from '../ticket-sales-campaigns-list-model';

import './ticket-sales-campaigns-list.css';

export interface TicketSalesCampaignsStatusTabsProps {
  active: CampaignListTab;
  counts: Record<CampaignListTab, number>;
  onChange: (next: CampaignListTab) => void;
  /** The id of the results these tabs filter. */
  panelId: string;
}

export function TicketSalesCampaignsStatusTabs({
  active,
  counts,
  onChange,
  panelId,
}: TicketSalesCampaignsStatusTabsProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (e: KeyboardEvent) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    e.preventDefault();
    const count = CAMPAIGN_TABS.length;
    const index = (CAMPAIGN_TABS.indexOf(active) + step + count) % count;
    onChange(CAMPAIGN_TABS[index]);
    tabRefs.current[index]?.focus();
  };

  return (
    <div
      className="nb-seg tsc-tabs"
      role="tablist"
      aria-label="Filter by status"
      onKeyDown={onKeyDown}
      style={{ flex: '0 0 auto' }}
    >
      {CAMPAIGN_TABS.map((tab, i) => {
        const selected = tab === active;
        return (
          <button
            key={tab}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
            }}
          >
            {CAMPAIGN_TAB_LABELS[tab]}
            <span
              className="nb-tabular"
              style={{ fontSize: 11, color: 'var(--nb-faint)', fontWeight: 700 }}
            >
              {counts[tab]}
            </span>
          </button>
        );
      })}
    </div>
  );
}
